// Editorial photo-driven hero (Girl Up inspired) — full-bleed photo with text overlay
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../contexts/LanguageContext";
import { t } from "../i18n/translations";

const HERO_PHOTO = "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/rbuyz7xl_image.png";

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const item = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const HeroAnimation = () => {
  const { lang } = useLang();
  const tr = t[lang];

  return (
    <section data-testid="hero-section" className="relative w-full overflow-hidden bg-[#0A192F]">
      <div className="relative w-full h-[78vh] min-h-[560px] max-h-[860px] img-parallax-wrap">
        <motion.img
          src={HERO_PHOTO}
          alt="Youth volunteer with elderly community member"
          className="absolute inset-0 w-full h-full object-cover img-parallax"
          initial={{ scale: 1.06, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/85 via-[#0A192F]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/40 via-transparent to-transparent" />

        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center">
          <motion.div className="max-w-2xl text-white" variants={stagger} initial="hidden" animate="visible">
            <motion.p variants={item} className={`inline-flex items-center font-bold text-[#F2B53A] border-l-4 border-[#E9A229] pl-3 py-1 ${lang === "zh" ? "text-base sm:text-lg tracking-[0.12em]" : "text-[11px] uppercase tracking-[0.28em]"}`}>
              {tr.hero.eyebrow}
            </motion.p>
            <motion.h1 variants={item} className={`mt-6 font-heading font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight whitespace-pre-line ${lang === "zh" ? "leading-[1.7]" : "leading-[1.05]"}`}>
              {tr.hero.headline}
            </motion.h1>
            <motion.p variants={item} className="mt-6 text-lg lg:text-xl text-slate-100/90 leading-relaxed max-w-xl">
              {tr.hero.sub}
            </motion.p>
            <motion.div variants={item} className="mt-9 flex flex-wrap gap-3">
              <a href={tr.volunteer.formUrl} target="_blank" rel="noreferrer" data-testid="hero-cta-volunteer" className="btn-pill inline-flex items-center bg-white text-[#0A192F] hover:bg-slate-100 px-7 py-3.5 rounded-full font-bold uppercase tracking-wide shadow-lg transition-all">
                {tr.hero.cta2}
              </a>
              <Link to="/contact?interest=sponsorship" data-testid="hero-cta-partner" className="btn-pill inline-flex items-center gap-2 bg-[#E9A229] hover:bg-[#C7841B] text-white px-7 py-3.5 rounded-full font-bold uppercase tracking-wide shadow-lg hover:shadow-xl transition-all">
                {tr.hero.cta1} <ArrowRight size={16} className="btn-arrow" />
              </Link>
              <Link to="/workshops" data-testid="hero-cta-explore" className="btn-pill inline-flex items-center text-white border-2 border-white/70 hover:bg-white hover:text-[#0A192F] px-7 py-3 rounded-full font-bold uppercase tracking-wide transition-all">
                {tr.hero.cta3}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroAnimation;
